import React, { useCallback } from 'react';

import { Box, Inline } from '@atlaskit/primitives';
import Skeleton from '@atlaskit/skeleton';

import { ContentOwnerItemWithChangeOwner } from '@confluence/content-ownership';
import { useContentStatus } from '@confluence/page-utils/entry-points/useContentStatus';

import { useSpaceAdmin } from '../hooks/useSpaceAdmin';
import { useContentOwner } from '../hooks/useContentOwner';

import { InlineRetryError } from './InlineRetryError';

export const OwnerSection = () => {
	const {
		type: contentType,
		contentStatus,
		loading: contentStatusLoading,
		error: contentStatusError,
		refetch: contentStatusRefetch,
	} = useContentStatus();
	const {
		data: contentOwnerData,
		loading: contentOwnerLoading,
		error: contentOwnerError,
		refetch: contentOwnerRefetch,
	} = useContentOwner();
	const {
		data: spaceAdminData,
		loading: spaceAdminLoading,
		error: spaceAdminError,
		refetch: spaceAdminRefetch,
	} = useSpaceAdmin();

	const error = contentStatusError || contentOwnerError || spaceAdminError;
	const refetch = useCallback(() => {
		if (contentStatusError) {
			void contentStatusRefetch();
		}
		if (contentOwnerError) {
			void contentOwnerRefetch();
		}
		if (spaceAdminError) {
			void spaceAdminRefetch();
		}
	}, [
		contentStatusError,
		contentStatusRefetch,
		contentOwnerError,
		contentOwnerRefetch,
		spaceAdminError,
		spaceAdminRefetch,
	]);

	const renderOwnerSectionContent = () => {
		if (contentStatusLoading || contentOwnerLoading || spaceAdminLoading) {
			return (
				<Inline alignBlock="center" space="space.150" testId="owner-section-loading">
					<Skeleton width="40px" height="40px" borderRadius={200} />
					<Skeleton width="154px" height="32px" borderRadius={4} />
				</Inline>
			);
		}

		if (error) {
			return <InlineRetryError error={error} refetch={refetch} />;
		}

		const { accountId, profilePicturePath, displayName } = contentOwnerData;
		return (
			<ContentOwnerItemWithChangeOwner
				ownerAccountId={accountId}
				ownerName={displayName}
				ownerAvatarUrl={profilePicturePath}
				contentType={contentType}
				contentStatus={contentStatus}
				isCurrentUserSpaceAdmin={spaceAdminData.isCurrentUserSpaceAdmin}
				analyticsSource="contentWrapper"
			/>
		);
	};

	return <Box testId="owner-section">{renderOwnerSectionContent()}</Box>;
};
